/**
 * 자동분류 추가 · 수정 팝업 (migration 161, 2026-09-05 사용자 지시).
 *
 * 메일주소(보낸사람 · 함께받는사람) · 메일제목 포함 · 모두/하나만 만족 · 처리방법 둘.
 *
 * ★★★ **주소는 정본 B2-2 「여러 값 입력」의 첫 사용**이다 — 치고 Enter 로 칩이 되고,
 *    칸을 떠날 때도 담긴다(치다 만 값을 버리지 않는다).
 * ★★ **조건도 처리도 최소 하나**는 있어야 한다. 조건이 없으면 「모든 메일」에 걸려 편지함이
 *    통째로 옮겨지고, 처리가 없으면 걸려도 달라지는 것이 없다. 서버도 막지만(400),
 *    여기서 먼저 막아야 **왜 안 되는지가 그 자리에** 보인다.
 * ★  메일함·태그가 하나도 없으면 체크박스를 **잠그고 어디서 만드는지 적는다**(정본 P3).
 */

import { useState } from 'react';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { MultiValueInput } from '../../components/shared/MultiValueInput';
import { Radio } from '../../components/shared/Radio';
import { Select } from '../../components/shared/Select';
import { tMail } from '../../lib/mail-i18n';
import type {
  MailFilterAddressKind, MailFilterMatchMode, MailFilterRule, MailFilterRuleInput,
  MailFolderNode, MailTag,
} from '../../api/mail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface MailFilterRuleModalProps {
  /** 고치는 중이면 그 규칙. 새로 만들면 null. */
  target?: MailFilterRule | null;
  folders: MailFolderNode[];
  tags: MailTag[];
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: MailFilterRuleInput) => void;
}

export function MailFilterRuleModal({
  target, folders, tags, busy, error, onClose, onSubmit,
}: MailFilterRuleModalProps) {
  const [name, setName] = useState(target?.name ?? '');
  const [addressKind, setAddressKind] = useState<MailFilterAddressKind>(target?.addressKind ?? 'FROM');
  const [addresses, setAddresses] = useState<string[]>(target?.addresses ?? []);
  const [subject, setSubject] = useState(target?.subjectContains ?? '');
  const [matchMode, setMatchMode] = useState<MailFilterMatchMode>(target?.matchMode ?? 'ALL');
  const [useFolder, setUseFolder] = useState(target?.folder != null);
  const [folderId, setFolderId] = useState<string>(target?.folder?.id ?? folders[0]?.id ?? '');
  const [useTag, setUseTag] = useState(target?.tag != null);
  const [tagId, setTagId] = useState<string>(target?.tag?.id ?? tags[0]?.id ?? '');
  const [local, setLocal] = useState<string | null>(null);

  const submit = () => {
    if (!name.trim()) { setLocal(tMail('mail.filterset.err_name')); return; }
    const subjectValue = subject.trim() === '' ? null : subject.trim();
    if (addresses.length === 0 && subjectValue === null) {
      setLocal(tMail('mail.filterset.err_cond'));
      return;
    }
    const folder = useFolder && folderId ? folderId : null;
    const tag = useTag && tagId ? tagId : null;
    if (folder === null && tag === null) { setLocal(tMail('mail.filterset.err_action')); return; }

    setLocal(null);
    onSubmit({
      name: name.trim(),
      addressKind,
      addresses,
      subjectContains: subjectValue,
      matchMode,
      folderId: folder,
      tagId: tag,
    });
  };

  return (
    <Modal
      title={target ? tMail('mail.filterset.edit_title') : tMail('mail.filterset.add_title')}
      size="md"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {tMail('mail.filterset.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {target ? tMail('mail.filterset.save') : tMail('mail.filterset.register')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        <FormField label={tMail('mail.filterset.f.name')} required>
          <Input
            value={name}
            maxLength={60}
            autoFocus
            placeholder={tMail('mail.filterset.f.name_ph')}
            onChange={(e) => { setName(e.target.value); if (local) setLocal(null); }}
            /* 한 줄 칸의 Enter 는 여기서 막는다 — 안 막으면 폼이 통째로 올라간다. */
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            fullWidth
          />
        </FormField>

        <FormField label={tMail('mail.filterset.f.address')}>
          <div className="filtrule__addr">
            <Select
              className="filtrule__kind"
              ariaLabel={tMail('mail.filterset.f.address')}
              value={addressKind}
              onChange={(v) => setAddressKind(v as MailFilterAddressKind)}
              options={[
                { value: 'FROM', label: tMail('mail.filterset.cond.from') },
                { value: 'TO', label: tMail('mail.filterset.cond.to') },
              ]}
            />
            <MultiValueInput
              className="filtrule__mvi"
              values={addresses}
              onChange={(next) => { setAddresses(next); if (local) setLocal(null); }}
              normalize={(v) => v.trim().toLowerCase()}
              validate={(v) => EMAIL_RE.test(v)}
              invalidMessage={tMail('mail.filterset.err_addr')}
              duplicateMessage={tMail('mail.filterset.err_dup')}
              removeLabel={tMail('mail.filterset.addr_remove')}
              placeholder={tMail('mail.filterset.f.address_ph')}
              ariaLabel={tMail('mail.filterset.f.address')}
              max={20}
            />
          </div>
        </FormField>

        <FormField label={tMail('mail.filterset.f.subject')}>
          <div className="filtrule__subj">
            <Input
              value={subject}
              maxLength={200}
              placeholder={tMail('mail.filterset.f.subject_ph')}
              onChange={(e) => { setSubject(e.target.value); if (local) setLocal(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
            <span className="filtrule__suffix">{tMail('mail.filterset.f.subject_suffix')}</span>
          </div>
        </FormField>

        <FormField label={tMail('mail.filterset.f.match')}>
          <div className="wc-set__inline" role="radiogroup" aria-label={tMail('mail.filterset.f.match')}>
            <Radio
              name="filtrule-match"
              checked={matchMode === 'ALL'}
              onChange={() => setMatchMode('ALL')}
              label={tMail('mail.filterset.cond.all')}
            />
            <Radio
              name="filtrule-match"
              checked={matchMode === 'ANY'}
              onChange={() => setMatchMode('ANY')}
              label={tMail('mail.filterset.cond.any')}
            />
          </div>
        </FormField>

        <FormField label={tMail('mail.filterset.f.action')}>
          <div className="filtrule__acts">
            <div className="filtrule__act">
              <Checkbox
                checked={useFolder}
                disabled={folders.length === 0}
                title={folders.length === 0 ? tMail('mail.filterset.f.no_folder') : undefined}
                onChange={(e) => { setUseFolder(e.target.checked); if (local) setLocal(null); }}
                label={tMail('mail.filterset.f.move')}
              />
              {useFolder && folders.length > 0 && (
                <Select
                  className="filtrule__pick"
                  ariaLabel={tMail('mail.filterset.f.folder_ph')}
                  value={folderId}
                  onChange={setFolderId}
                  options={folders.map((f) => ({ value: f.id, label: f.name }))}
                />
              )}
            </div>
            {folders.length === 0 && <p className="wc-set__hint">{tMail('mail.filterset.f.no_folder')}</p>}

            <div className="filtrule__act">
              <Checkbox
                checked={useTag}
                disabled={tags.length === 0}
                title={tags.length === 0 ? tMail('mail.filterset.f.no_tag') : undefined}
                onChange={(e) => { setUseTag(e.target.checked); if (local) setLocal(null); }}
                label={tMail('mail.filterset.f.tag')}
              />
              {useTag && tags.length > 0 && (
                <Select
                  className="filtrule__pick"
                  ariaLabel={tMail('mail.filterset.f.tag_ph')}
                  value={tagId}
                  onChange={setTagId}
                  options={tags.map((t) => ({ value: t.id, label: t.name }))}
                />
              )}
            </div>
            {tags.length === 0 && <p className="wc-set__hint">{tMail('mail.filterset.f.no_tag')}</p>}
          </div>
        </FormField>

        {(local ?? error) && <p className="mboxmodal__err" role="alert">{local ?? error}</p>}
      </div>
    </Modal>
  );
}
